import {
  FormControl,
  ValidationErrors
} from '@angular/forms';
import {
  Subject,
  Subscription,
  merge,
  BehaviorSubject
} from 'rxjs';
import { FormControlState } from 'ngrx-forms';
import {
  FORM_CONTROL_DISABLED_KEY,
  FORM_CONTROL_READONLY_KEY
} from './form-control';

export const READONLY = 'readonly';

export class WrappedFormControl<
  V,
  F extends FormControlState<any> = FormControlState<any>
> extends FormControl {
  private _ngrxFormControl: F | null = null;

  public get ngrxFormControl(): F | null {
    return this._ngrxFormControl;
  }

  public set ngrxFormControl(ngrxFormControl: F | null) {
    this._ngrxFormControl = ngrxFormControl;
    if (ngrxFormControl) {
      if (ngrxFormControl.isTouched) {
        // call super instead of this to prevent loop
        super.markAsTouched({ onlySelf: true });
      }
      if (ngrxFormControl.isUntouched) {
        this.markAsUntouched({ onlySelf: true });
      }
      if (ngrxFormControl.isDirty) {
        this.markAsDirty({ onlySelf: true });
      }
      if (ngrxFormControl.isPristine) {
        this.markAsPristine({ onlySelf: true });
      }
      if (ngrxFormControl.isValid !== this.valid) {
        this.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
      if (
        ngrxFormControl.errors &&
        Object.keys(ngrxFormControl.errors).length &&
        !this.errors
      ) {
        this.setErrors(ngrxFormControl.errors, { emitEvent: false });
      }
      if (
        ngrxFormControl.userDefinedProperties.hasOwnProperty(
          FORM_CONTROL_DISABLED_KEY
        )
      ) {
        if (ngrxFormControl.userDefinedProperties[FORM_CONTROL_DISABLED_KEY]) {
          this.disable({ onlySelf: true });
        } else {
          this.enable({ onlySelf: true });
        }
      }
      if (
        ngrxFormControl.userDefinedProperties.hasOwnProperty(
          FORM_CONTROL_READONLY_KEY
        )
      ) {
        this.readonly =
          ngrxFormControl.userDefinedProperties[FORM_CONTROL_READONLY_KEY];
        this.readonlyChanges$.next(this.readonly);
      }
      // TODO : remove deprecated code
      if (ngrxFormControl.userDefinedProperties.hasOwnProperty(READONLY)) {
        this.readonly = !!ngrxFormControl.userDefinedProperties[READONLY];
        this.readonlyChanges$.next(this.readonly);
      }
    }
  }

  public boxed = false;

  public ngrxTouchedChanges$: Subject<boolean> = new Subject<boolean>();
  public statusChanges$: Subject<string> = new Subject();
  public disabledChanges$: Subject<boolean> = new Subject();
  public readonlyChanges$: Subject<boolean> = new Subject();
  public ngrxValueChanges$: Subject<V> = new BehaviorSubject<V>(null);
  public ngrxValue: V | null = null;
  public standalone = false;
  public readonly = false;
  protected _subscription = new Subscription();
  protected _setValue;

  public constructor(init?: V) {
    super(init);
    const ngrxValidator = this.ngrxValidator.bind(this);
    this.setValidators(() => ngrxValidator());
    this._subscription.add(
      this.ngrxValueChanges$.subscribe(ngrxValue => {
        this.ngrxValue = ngrxValue;
      })
    );
    this._setValue = super.setValue;
    this.registerOnDisabledChange(isDisabled =>
      this.disabledChanges$.next(isDisabled)
    );
    this._subscription.add(
      merge(
        this.ngrxValueChanges$,
        this.statusChanges,
        this.valueChanges,
        this.disabledChanges$,
        this.readonlyChanges$ // TODO : add action to copyStatus
      ).subscribe(() => this.statusChanges$.next())
    );
  }

  public addError(key: string, value: any): void {
    this.setErrors({ ...(this.errors || {}), [key]: value });
  }

  public removeError(key: string): void {
    if (this.errors && this.errors.hasOwnProperty(key)) {
      const errors = { ...this.errors };
      delete errors[key];
      if (Object.keys(errors).length === 0) {
        this.setErrors(null);
      } else {
        this.setErrors(errors);
      }
    }
  }

  public isEqual(a: any, b: any): boolean {
    if (
      this.boxed &&
      a &&
      b &&
      a.hasOwnProperty('id') &&
      b.hasOwnProperty('id')
    ) {
      return (a as any).id === (b as any).id;
    }
    if (
      this.boxed &&
      a &&
      b &&
      a.hasOwnProperty('_id') &&
      b.hasOwnProperty('_id')
    ) {
      return (a as any)._id === (b as any)._id;
    }
    return a === b;
  }

  public hasChanged(a: V, b: V = this.ngrxValue): boolean {
    if (this.boxed && a && b && Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return true;
      }
      return a.some(v => !this.isEqual(v, b));
    }
    return !this.isEqual(b, a);
  }

  public toDisplayValue(value: V): any {
    if (typeof value === 'object') {
      if (!value) {
        return '';
      } else if (value.hasOwnProperty('name')) {
        return (value as any).name;
      } else if (value.hasOwnProperty('toString')) {
        return value.toString();
      } else {
        return JSON.stringify(value);
      }
    } else if (value === undefined) {
      return '';
    } else if (typeof value === 'number') {
      return value;
    } else if (typeof value === 'boolean') {
      return value;
    } else {
      return value.toString();
    }
  }

  public toNgrxValue<T = V>(value: T): V {
    return value as any;
  }

  public destroy(): void {
    this._subscription.unsubscribe();
  }

  public removeFromArray(item: any): void {
    if (!Array.isArray(this.ngrxValue)) {
      if (this.ngrxValue) {
        throw new Error(
          'removeFromArray only allowed on controls with array values'
        );
      } else {
        console.warn('Control value is null or undefined');
        // return bs an item can not be removed from an empty array
        return;
      }
    }
    const values = [...(this.ngrxValue || [])];
    const index = values.findIndex(value => this.isEqual(item, value));
    if (index === -1) {
      console.warn('To removed item not found', { values, item });
      return;
    }
    values.splice(index, 1);
    this.setValue(values as any);
  }

  public addToArray(item: any, end = true): void {
    if (!Array.isArray(this.ngrxValue)) {
      if (this.ngrxValue) {
        throw new Error(
          'addToArray only allowed on controls with array values'
        );
      } else {
        console.warn('Control value is null or undefined');
      }
    }
    if (end) {
      this.setValue([...((this.ngrxValue as any) || []), item] as any);
    } else {
      this.setValue([item, ...((this.ngrxValue as any) || [])] as any);
    }
  }

  public ngrxValidator(): ValidationErrors | null {
    if (this._ngrxFormControl) {
      if (Object.keys(this._ngrxFormControl.errors).length) {
        return this._ngrxFormControl.errors;
      } else {
        return null;
      }
    } else if (!this.standalone) {
      console.warn('ngrx control is used as standalone');
    }
    return null;
  }

  markAsDirty(opts?: { onlySelf?: boolean }): void {
    super.markAsDirty(opts);
    this.statusChanges$.next();
  }

  markAsPending(opts?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.markAsPending(opts);
    this.statusChanges$.next();
  }

  markAsPristine(opts?: { onlySelf?: boolean }): void {
    super.markAsPristine(opts);
    this.statusChanges$.next();
  }

  markAsTouched({ onlySelf }: { onlySelf?: boolean } = {}): void {
    super.markAsTouched({ onlySelf });

    this.ngrxTouchedChanges$.next(true);
    this.statusChanges$.next();
  }

  markAsUntouched(opts?: { onlySelf?: boolean }): void {
    super.markAsUntouched(opts);
    this.statusChanges$.next();
  }

  public setValue(
    value: V | null,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
      emitModelToViewChange?: boolean;
      emitViewToModelChange?: boolean;
    }
  ): void {
    const ngrxValue = this.toNgrxValue(value);
    const displayValue = this.toDisplayValue(value);
    if (
      !options ||
      options.emitEvent === undefined ||
      options.emitEvent === true
    ) {
      if (this.hasChanged(ngrxValue)) {
        this.ngrxValueChanges$.next(ngrxValue);
      }
    } else {
      this.ngrxValue = ngrxValue;
    }
    super.setValue(displayValue, options);
  }

  public copyStatus(formControl: FormControl): void {
    if (this.touched) {
      formControl.markAsTouched({ onlySelf: true });
    }
    if (this.untouched) {
      formControl.markAsUntouched({ onlySelf: true });
    }
    if (this.dirty) {
      formControl.markAsDirty({ onlySelf: true });
    }
    if (this.pristine) {
      formControl.markAsPristine({ onlySelf: true });
    }
    if (this.pending) {
      formControl.markAsPending({ onlySelf: true });
    }
    if (this.disabled) {
      formControl.disable({ onlySelf: true });
    } else {
      formControl.enable({ onlySelf: true });
    }
    formControl.setErrors(this.errors);
  }
}
